import {
  CreateInstanceRequest,
  CreateInstanceResponse,
  ConnectionState,
  SendTextMessageRequest,
  SendMediaMessageRequest,
  SendButtonMessageRequest,
  MessageResponse,
  FindMessagesRequest,
  FindMessagesResponse,
  SetWebhookRequest,
  InstanceStatus,
  EvolutionError
} from '@/types/evolution-v2.types'
import { supabase } from '@/lib/supabase'
import { env } from '@/utils/env'
import logger from '@/utils/logger'

export class EvolutionV2Service {
  private baseUrl: string
  private globalApiKey: string

  constructor() {
    this.baseUrl = env.VITE_EVOLUTION_V2_URL
    this.globalApiKey = env.VITE_EVOLUTION_V2_APIKEY
  }

  /**
   * Busca nomes das instâncias ativas do banco de dados
   * @param moduleType - Se fornecido, filtra por module_type ('revendas' | 'certificados' | 'comercial')
   */
  private async getInstanceNamesFromDB(moduleType?: string): Promise<string[]> {
    try {
      // Sempre buscar module_type para validação em camada de aplicação
      let query = supabase
        .from('whatsapp_instances')
        .select('instance_id, name, module_type')
        .eq('is_active', true)

      if (moduleType) {
        query = query.eq('module_type', moduleType)
      }

      const { data, error } = await query

      if (error || !data) return []

      // Validação em camada de aplicação: garantir que o filtro de module_type foi aplicado
      const filtered = moduleType
        ? data.filter(i => i.module_type === moduleType)
        : data

      if (moduleType && filtered.length !== data.length) {
        logger.warn(
          `⚠️ [Evolution] Filtro module_type="${moduleType}" não aplicado corretamente no DB.`,
          `DB retornou ${data.length} instâncias, após filtro local: ${filtered.length}.`,
          'Instâncias retornadas:', data.map(i => `${i.name} (${i.module_type})`)
        )
      }

      return filtered.map(i => i.instance_id || i.name)
    } catch {
      return []
    }
  }

  private getHeaders(apiKey?: string): HeadersInit {
    return {
      'Content-Type': 'application/json',
      'apikey': apiKey || this.globalApiKey
    }
  }

  private async makeRequest<T>(
    method: string,
    endpoint: string,
     
    data?: any,
    apiKey?: string
  ): Promise<T> {
    // encodeURI preserva / mas codifica espaços e caracteres especiais no nome da instância
    const url = encodeURI(`${this.baseUrl}${endpoint}`)

    logger.debug(`Evolution V2 API Request: ${method} ${url}`, data)

    try {
      const response = await fetch(url, {
        method,
        headers: this.getHeaders(apiKey),
        body: data ? JSON.stringify(data) : undefined
      })

      const responseData = await response.json() as Record<string, unknown>

      if (!response.ok) {
        // Tratar diferentes formatos de erro da API
        let errorMessage = 'Unknown error'
        if (typeof responseData.error === 'string') {
          errorMessage = responseData.error
        } else if (responseData.error && typeof responseData.error === 'object' && 'message' in responseData.error) {
          errorMessage = (responseData.error as Record<string, unknown>).message as string
        } else if (typeof responseData.message === 'string') {
          errorMessage = responseData.message
        }

        const error: EvolutionError = {
          error: { message: errorMessage },
          status: response.status
        }
        logger.error('Evolution V2 API Error:', error)
        throw new Error(errorMessage)
      }

      logger.debug(`Evolution V2 API Response: ${method} ${url}`, responseData)
      return responseData as T
    } catch (err: unknown) {
      logger.error('Evolution V2 Request Error:', err)
      throw err
    }
  }

  // 1. GERENCIAMENTO DE INSTÂNCIAS

  /**
   * Cria uma nova instância
   */
  async createInstance(params: CreateInstanceRequest): Promise<CreateInstanceResponse> {
    return this.makeRequest<CreateInstanceResponse>(
      'POST',
      '/instance/create',
      params
    )
  }

  /**
   * Conecta uma instância existente
   */
  async connectInstance(instanceName: string): Promise<InstanceStatus> {
    return this.makeRequest<InstanceStatus>(
      'GET',
      `/instance/connect/${instanceName}`
    )
  }

  /**
   * Obtém o status de conexão de uma instância
   */
  async getConnectionState(instanceName: string, apiKey?: string): Promise<ConnectionState> {
    return this.makeRequest<ConnectionState>(
      'GET',
      `/instance/connectionState/${instanceName}`,
      undefined,
      apiKey
    )
  }

  /**
   * Obtém informações de todas as instâncias
   */
  async fetchInstances(): Promise<InstanceStatus[]> {
    return this.makeRequest<InstanceStatus[]>(
      'GET',
      '/instance/fetchInstances'
    )
  }

  /**
   * Alias para fetchInstances - usado pelo InstanceManager
   */
  async getAllInstances(): Promise<InstanceStatus[]> {
    return this.fetchInstances()
  }

  /**
   * Reinicia uma instância
   */
  async restartInstance(instanceName: string): Promise<unknown> {
    return this.makeRequest(
      'PUT',
      `/instance/restart/${instanceName}`
    )
  }

  /**
   * Desconecta uma instância
   */
  async logoutInstance(instanceName: string): Promise<unknown> {
    return this.makeRequest(
      'DELETE',
      `/instance/logout/${instanceName}`
    )
  }

  /**
   * Deleta uma instância
   */
  async deleteInstance(instanceName: string): Promise<unknown> {
    return this.makeRequest(
      'DELETE',
      `/instance/delete/${instanceName}`
    )
  }

  /**
   * Atualiza settings de uma instância
   */
  async updateInstanceSettings(instanceName: string, settings: Record<string, unknown>): Promise<unknown> {
    return this.makeRequest(
      'POST',
      `/settings/set/${instanceName}`,
      settings
    )
  }

  /**
   * Reconfigura rejectCall=false em todas as instâncias ativas
   */
  async disableRejectCallAllInstances(): Promise<{ success: string[]; failed: string[] }> {
    const instances = await this.getInstanceNamesFromDB()
    const success: string[] = []
    const failed: string[] = []

    for (const instanceName of instances) {
      try {
        await this.updateInstanceSettings(instanceName, {
          rejectCall: false,
          msgCall: '',
          groupsIgnore: false,
          alwaysOnline: true,
          readMessages: false,
          readStatus: false,
          syncFullHistory: false
        })
        success.push(instanceName)
        logger.debug(`✅ rejectCall desabilitado em: ${instanceName}`)
      } catch (err: unknown) {
        failed.push(instanceName)
        logger.error(`❌ Falha ao desabilitar rejectCall em: ${instanceName}`, err)
      }
    }

    logger.info(`📞 rejectCall atualizado: ${success.length} OK, ${failed.length} falhas`)
    return { success, failed }
  }

  /**
   * Obtém QR Code para conexão da instância
   */
  async getQRCode(instanceName: string): Promise<{ base64?: string; code?: string; qrcode?: string }> {
    return this.makeRequest<{ base64?: string; code?: string; qrcode?: string }>(
      'GET',
      `/instance/connect/${instanceName}`
    )
  }

  /**
   * Desconecta uma instância (logout)
   */
  async disconnectInstance(instanceName: string): Promise<unknown> {
    return this.logoutInstance(instanceName)
  }

  // 2. ENVIO DE MENSAGENS

  /**
   * Envia mensagem de texto
   */
  async sendTextMessage(
    instanceName: string,
    params: SendTextMessageRequest,
    apiKey?: string
  ): Promise<MessageResponse> {
    return this.makeRequest<MessageResponse>(
      'POST',
      `/message/sendText/${instanceName}`,
      params,
      apiKey
    )
  }

  /**
   * @deprecated Use sendTextMessage instead
   * Alias para retrocompatibilidade
   */
  async sendText(params: {
    instanceName: string
    number: string
    text: string
  }): Promise<MessageResponse> {
    return this.sendTextMessage(params.instanceName, {
      number: params.number,
      text: params.text
    })
  }

  /**
   * Envia mídia (imagem, vídeo, áudio, documento)
   */
  async sendMediaMessage(
    instanceName: string,
    params: SendMediaMessageRequest,
    apiKey?: string
  ): Promise<MessageResponse> {
    return this.makeRequest<MessageResponse>(
      'POST',
      `/message/sendMedia/${instanceName}`,
      params,
      apiKey
    )
  }

  /**
   * Envia mensagem com botões interativos (WhatsApp Business)
   *
   * @param instanceName - Nome da instância Evolution API
   * @param params - Parâmetros da mensagem com botões
   * @param apiKey - API Key opcional (usa global por padrão)
   * @returns Resposta com key.id da mensagem enviada
   *
   * @example
   * ```ts
   * const response = await service.sendButtonMessage('Jorge Vr Tech', {
   *   number: '5524993062444',
   *   title: 'Nova Tarefa Financeira',
   *   description: 'Empresa: VR Tech\nTítulo: Pagamento de fornecedor',
   *   footer: 'Sistema de Gestão',
   *   buttons: [
   *     { title: 'reply', displayText: 'Ciente', id: 'btn_ciente' },
   *     { title: 'reply', displayText: 'Feito', id: 'btn_feito' },
   *     { title: 'reply', displayText: 'Negado', id: 'btn_negado' }
   *   ]
   * })
   *
   * logger.debug('Message ID:', response.key.id) // Usado para correlação
   * ```
   */
  async sendButtonMessage(
    instanceName: string,
    params: SendButtonMessageRequest,
    apiKey?: string
  ): Promise<MessageResponse> {
    logger.debug(`📤 [Evolution] Enviando mensagem com botões para ${params.number}`)
    logger.debug(`📋 [Evolution] Título: ${params.title}`)
    logger.debug(`🔘 [Evolution] Botões: ${params.buttons.map(b => b.displayText).join(', ')}`)

    return this.makeRequest<MessageResponse>(
      'POST',
      `/message/sendButtons/${instanceName}`,
      params,
      apiKey
    )
  }

  /**
   * Envia mensagem usando a melhor instância disponível
   * @param params.moduleType - Se fornecido, filtra instância por module_type
   */
  async sendMessage(params: {
    number: string
    text: string
    quoted?: SendTextMessageRequest['quoted']
    moduleType?: string
  }): Promise<{ instance: string; response: MessageResponse }> {
    const availableInstance = await this.getAvailableInstance(params.moduleType)

    if (!availableInstance) {
      throw new Error(params.moduleType
        ? `Nenhuma instância disponível para o módulo "${params.moduleType}". Verifique as configurações de instâncias WhatsApp.`
        : 'Nenhuma instância disponível para envio')
    }

    const response = await this.sendTextMessage(availableInstance, {
      number: params.number,
      text: params.text,
      quoted: params.quoted
    })

    return {
      instance: availableInstance,
      response
    }
  }

  /**
   * Envia mídia usando a melhor instância disponível
   * @param params.moduleType - Se fornecido, filtra instância por module_type
   */
  async sendMedia(params: {
    number: string
    media: string
    mediatype: 'image' | 'video' | 'audio' | 'document'
    caption?: string
    fileName?: string
    moduleType?: string
  }): Promise<{ instance: string; response: MessageResponse }> {
    const availableInstance = await this.getAvailableInstance(params.moduleType)

    if (!availableInstance) {
      throw new Error(params.moduleType
        ? `Nenhuma instância disponível para o módulo "${params.moduleType}". Verifique as configurações de instâncias WhatsApp.`
        : 'Nenhuma instância disponível para envio')
    }

    const response = await this.sendMediaMessage(availableInstance, params)

    return {
      instance: availableInstance,
      response
    }
  }

  // 3. BUSCA DE MENSAGENS

  /**
   * Busca mensagens de uma conversa
   */
  async findMessages(
    instanceName: string,
    params: FindMessagesRequest,
    apiKey?: string
  ): Promise<FindMessagesResponse> {
    // Tentar diferentes endpoints conhecidos da Evolution API
    const possibleEndpoints = [
      `/chat/findMessages/${instanceName}`,
      `/message/findMessages/${instanceName}`,
      `/chat/find/${instanceName}`,
      `/message/find/${instanceName}` // endpoint original
    ]

    let lastError: unknown
    
    for (const endpoint of possibleEndpoints) {
      try {
        logger.debug(`🔍 [Evolution] Tentando endpoint: ${endpoint}`)
        return await this.makeRequest<FindMessagesResponse>(
          'POST',
          endpoint,
          params,
          apiKey
        )
      } catch (err: unknown) {
        lastError = err
        const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido'
        logger.debug(`❌ [Evolution] Endpoint ${endpoint} falhou:`, errorMessage)

        // Se não for 404, parar de tentar outros endpoints
        if (!errorMessage.includes('404') && !errorMessage.includes('Not Found')) {
          throw err
        }
      }
    }
    
    // Se chegou aqui, todos os endpoints falharam
    logger.error('❌ [Evolution] Todos os endpoints falharam. Último erro:', lastError)
    throw lastError
  }

  /**
   * Busca mensagens de um número específico
   */
  async getMessagesFromNumber(
    instanceName: string,
    number: string,
    limit: number = 50
  ): Promise<FindMessagesResponse> {
    return this.findMessages(instanceName, {
      where: {
        key: {
          remoteJid: number
        }
      },
      limit
    })
  }

  // 4. CONFIGURAÇÃO DE WEBHOOK

  /**
   * Configura webhook para uma instância
   */
  async setWebhook(
    instanceName: string,
    params: SetWebhookRequest,
    apiKey?: string
  ): Promise<unknown> {
    return this.makeRequest(
      'POST',
      `/webhook/set/${instanceName}`,
      params,
      apiKey
    )
  }

  /**
   * Obtém configuração do webhook
   */
  async getWebhook(instanceName: string, apiKey?: string): Promise<unknown> {
    return this.makeRequest(
      'GET',
      `/webhook/find/${instanceName}`,
      undefined,
      apiKey
    )
  }

  // 5. GESTÃO DE CONVERSAS

  /**
   * Marca conversa como lida
   */
  async markChatAsRead(
    instanceName: string,
    remoteJid: string,
    apiKey?: string
  ): Promise<unknown> {
    return this.makeRequest(
      'PUT',
      `/chat/markAsRead/${instanceName}`,
      { remoteJid, status: 'read' },
      apiKey
    )
  }

  /**
   * Arquiva uma conversa
   */
  async archiveChat(
    instanceName: string,
    remoteJid: string,
    archive: boolean = true,
    apiKey?: string
  ): Promise<unknown> {
    return this.makeRequest(
      'PUT',
      `/chat/archive/${instanceName}`,
      { remoteJid, archive },
      apiKey
    )
  }

  /**
   * Deleta uma mensagem
   */
  async deleteMessage(
    instanceName: string,
    remoteJid: string,
    messageId: string,
    fromMe: boolean,
    apiKey?: string
  ): Promise<unknown> {
    return this.makeRequest(
      'DELETE',
      `/chat/deleteMessageForEveryone/${instanceName}`,
      { remoteJid, fromMe, id: messageId },
      apiKey
    )
  }

  /**
   * Deleta uma mensagem para todos (alias usado pelos módulos de atendimento)
   * Chama a Evolution API e marca como deletada no banco
   */
  async deleteMessageForEveryone(
    instanceName: string,
    messageId: string,
    remoteJid: string,
    fromMe: boolean
  ): Promise<boolean> {
    try {
      await this.makeRequest(
        'DELETE',
        `/chat/deleteMessageForEveryone/${instanceName}`,
        { id: messageId, remoteJid, fromMe }
      )
      return true
    } catch (err: unknown) {
      logger.error('[EvolutionV2] Erro ao deletar mensagem:', err)
      return false
    }
  }

  // 6. UTILITÁRIOS E HELPERS

  /**
   * Verifica se um número é válido no WhatsApp
   */
  async checkWhatsAppNumber(
    instanceName: string,
    numbers: string[],
    apiKey?: string
  ): Promise<unknown> {
    return this.makeRequest(
      'POST',
      `/chat/whatsappNumbers/${instanceName}`,
      { numbers },
      apiKey
    )
  }

  /**
   * Obtém a melhor instância disponível para envio
   * @param moduleType - Se fornecido, filtra por module_type ('revendas' | 'certificados' | 'comercial')
   */
  async getAvailableInstance(moduleType?: string): Promise<string | null> {
    const instances = await this.getInstanceNamesFromDB(moduleType)
    logger.debug(`🔍 [Evolution] getAvailableInstance(${moduleType || 'all'}): candidatas = [${instances.join(', ')}]`)

    for (const instance of instances) {
      try {
        const state = await this.getConnectionState(instance)
        if (state.instance.state === 'open') {
          logger.debug(`✅ [Evolution] Instância "${instance}" conectada (módulo: ${moduleType || 'all'})`)
          return instance
        }
        logger.debug(`⏭️ [Evolution] Instância "${instance}" não conectada (state: ${state.instance.state})`)
      } catch (err: unknown) {
        logger.warn(`⚠️ [Evolution] Instância "${instance}" não disponível:`, err)
        continue
      }
    }

    logger.warn(`❌ [Evolution] Nenhuma instância disponível para módulo "${moduleType || 'all'}"`)
    return null
  }

  /**
   * Obtém status de todas as instâncias configuradas
   */
  async getAllInstancesStatus(): Promise<Record<string, ConnectionState>> {
    const instances = await this.getInstanceNamesFromDB()
    const statuses: Record<string, ConnectionState> = {}

    for (const instance of instances) {
      try {
        statuses[instance] = await this.getConnectionState(instance)
      } catch (err: unknown) {
        statuses[instance] = {
          instance: {
            instanceName: instance,
            state: 'close'
          }
        }
      }
    }

    return statuses
  }

  /**
   * Configura webhooks para todas as instâncias
   */
  async setupAllWebhooks(webhookUrl: string): Promise<void> {
    const instances = await this.getInstanceNamesFromDB()
    const events: SetWebhookRequest['events'] = [
      'MESSAGES_UPSERT',
      'MESSAGES_UPDATE',
      'CONNECTION_UPDATE',
      'QRCODE_UPDATED'
    ]

    for (const instance of instances) {
      try {
        await this.setWebhook(instance, {
          url: webhookUrl,
          enabled: true,
          webhookByEvents: true,
          events
        })
        logger.debug(`Webhook configurado para ${instance}`)
      } catch (err: unknown) {
        logger.error(`Erro ao configurar webhook para ${instance}:`, err)
      }
    }
  }

  /**
   * Formata número para padrão WhatsApp
   */
  formatWhatsAppNumber(number: string): string {
    // Remove todos os caracteres não numéricos
    const cleaned = number.replace(/\D/g, '')
    
    // Se começar com 0, remove
    const withoutLeadingZero = cleaned.replace(/^0+/, '')
    
    // Se não tem código do país, adiciona 55 (Brasil)
    if (withoutLeadingZero.length === 10 || withoutLeadingZero.length === 11) {
      return `55${withoutLeadingZero}@s.whatsapp.net`
    }
    
    // Se já tem código do país
    if (withoutLeadingZero.length >= 12) {
      return `${withoutLeadingZero}@s.whatsapp.net`
    }
    
    throw new Error('Número de telefone inválido')
  }

  /**
   * Extrai número limpo do JID do WhatsApp
   */
  extractNumberFromJid(jid: string): string {
    return jid.replace('@s.whatsapp.net', '').replace('@c.us', '').replace('@g.us', '')
  }

  // 7. GESTÃO DE GRUPOS

  /**
   * Busca informações de um grupo específico (metadata)
   */
  async fetchGroupInfo(
    instanceName: string,
    groupJid: string,
    apiKey?: string
  ): Promise<unknown> {
    try {
      return await this.makeRequest(
        'GET',
        `/group/fetchGroupInfo/${instanceName}/${groupJid}`,
        undefined,
        apiKey
      )
    } catch (err: unknown) {
      logger.error(`❌ [Evolution] Erro ao buscar info do grupo ${groupJid}:`, err)
      throw err
    }
  }

  /**
   * Lista todos os grupos da instância
   */
  async fetchAllGroups(
    instanceName: string,
    apiKey?: string
  ): Promise<unknown> {
    try {
      return await this.makeRequest(
        'GET',
        `/group/fetchAllGroups/${instanceName}`,
        undefined,
        apiKey
      )
    } catch (err: unknown) {
      logger.error(`❌ [Evolution] Erro ao buscar grupos:`, err)
      throw err
    }
  }

  /**
   * Obtém participantes de um grupo
   */
  async fetchGroupParticipants(
    instanceName: string,
    groupJid: string,
    apiKey?: string
  ): Promise<unknown> {
    try {
      return await this.makeRequest(
        'GET',
        `/group/fetchParticipants/${instanceName}/${groupJid}`,
        undefined,
        apiKey
      )
    } catch (err: unknown) {
      logger.error(`❌ [Evolution] Erro ao buscar participantes do grupo:`, err)
      throw err
    }
  }

  /**
   * Retry com backoff exponencial
   */
  async retryWithBackoff<T>(
    operation: () => Promise<T>,
    maxRetries: number = 3,
    baseDelay: number = 1000
  ): Promise<T> {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await operation()
      } catch (err: unknown) {
        if (attempt === maxRetries) {
          throw err
        }
        
        const delay = baseDelay * Math.pow(2, attempt - 1)
        logger.warn(`Tentativa ${attempt} falhou, tentando novamente em ${delay}ms:`, err)
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }
    
    throw new Error('Todas as tentativas falharam')
  }

  /**
   * Converte arquivo para base64
   */
  async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => {
        const result = reader.result as string
        // Remove o prefixo data:type;base64,
        const base64 = result.split(',')[1]
        resolve(base64)
      }
      reader.onerror = error => reject(error)
    })
  }
}